"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import { Button } from "@batch-and-bowl/ui/button";
import "./styles/index.css";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Inner content, kept separate from the <html>/<body> wrapper below so it can
 * be unit-tested directly (rendering <html> through @testing-library/react
 * in jsdom produces DOM-nesting warnings). Reports the error to Sentry on
 * mount and renders the fallback UI.
 */
export function GlobalErrorContent({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div
      role="alert"
      className="flex min-h-svh flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <h2 className="text-lg">Something went wrong</h2>
      <p className="text-muted-foreground">
        Reload the page to continue. We&apos;ve been notified.
      </p>
      <Button onClick={reset}>Try again</Button>
    </div>
  );
}

/**
 * Next.js App Router global error boundary — catches errors that escape the
 * root layout. Per Next.js convention this file must render its own
 * <html>/<body> since it replaces the root layout entirely while active.
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  return (
    <html lang="en">
      <body>
        <GlobalErrorContent error={error} reset={reset} />
      </body>
    </html>
  );
}
